import React, { useContext, useEffect, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { Link, useLocation } from "react-router-dom";
import { getMenuList } from "./Menu";
import { MenuSuperAdmin } from "./MenuSuperAdmin";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { ThemeContext } from "../../../context/ThemeContext";
import logo from "../../../images/logo.png";
import { FaBars, FaTimes, FaAngleLeft } from "react-icons/fa";

const SideBar = ({ state, setState, handleActive, closeSidebar, isSidebarCollapsed, setIsSidebarCollapsed }) => {
  const {
    iconHover,
    sidebarposition,
    headerposition,
    sidebarLayout,
    ChangeIconSidebar,
  } = useContext(ThemeContext);

  const [adminMenuList, setAdminMenuList] = useState(() => getMenuList());
  const [hideOnScroll, setHideOnScroll] = useState(true);
  const [expandedItems, setExpandedItems] = useState(() => {
    const location = window.location.pathname;
    const pathToCheck = location.split("/")[1];
    const currentMenu = pathToCheck === "super-admin" ? MenuSuperAdmin : getMenuList();
    
    const allExpanded = {};
    currentMenu.forEach(item => {
      if (item.content && item.content.length > 0) {
        allExpanded[item.title] = true;
      }
    });
    
    return allExpanded;
  });
  const [hoveredItem, setHoveredItem] = useState(null);
  const [mobileSidebarCollapsed, setMobileSidebarCollapsed] = useState(false);

  useEffect(() => {
    const handleLabelUpdate = () => setAdminMenuList(getMenuList());
    window.addEventListener("jobTitleLabelUpdated", handleLabelUpdate);
    return () => window.removeEventListener("jobTitleLabelUpdated", handleLabelUpdate);
  }, []);

  useScrollPosition(
    ({ prevPos, currPos }) => {
      const isShow = currPos.y > prevPos.y;
      if (isShow !== hideOnScroll) {
        setHideOnScroll(isShow);
      }
    },
    [hideOnScroll]
  );

  const handleMenuActive = (status, parent) => {
    setState({ active: parent, activeMenu: status });
  };

  const toggleSubmenu = (title) => {
    setExpandedItems(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const toggleSidebar = (e) => {
    e.stopPropagation();
    
    if (typeof setIsSidebarCollapsed === 'function') {
      setIsSidebarCollapsed(!isSidebarCollapsed);
      const mainWrapper = document.querySelector('#main-wrapper');
      if (mainWrapper) {
        mainWrapper.classList.toggle('menu-toggle');
      }
    }
  };

  const toggleMobileSidebar = (e) => {
    e.stopPropagation();
    setIsSidebarCollapsed(!isSidebarCollapsed);
    const mainWrapper = document.querySelector('#main-wrapper');
    if (mainWrapper) {
      mainWrapper.classList.toggle('menu-toggle');
    }
  };

  const location = useLocation();
  const pathToCheck = location.pathname.split("/")[1];
  const currentMenu = pathToCheck === "super-admin" ? MenuSuperAdmin : adminMenuList;

  useEffect(() => {
    let activeName = "";
    let activeMenuName = "";

    currentMenu.forEach((menuItem) => {
      if (location.pathname === `/${menuItem.to}`) {
        activeName = menuItem.title;
      }

      if (menuItem.content) {
        menuItem.content.forEach((submenuItem) => {
          if (location.pathname.includes(submenuItem.to)) {
            activeName = menuItem.title;
            activeMenuName = submenuItem.title;
          }
        });
      }
    });

    if (location.pathname === "/" || (!activeName && !activeMenuName)) {
      activeName = "Dashboard";
    }

    handleActive(activeName, activeMenuName);
  }, [location, currentMenu]);

  useEffect(() => {
    const newExpandedItems = { ...expandedItems };
    currentMenu.forEach(item => {
      if (item.content && item.content.length > 0) {
        if (newExpandedItems[item.title] === undefined) {
          newExpandedItems[item.title] = true;
        }
      }
    });
    setExpandedItems(newExpandedItems);
  }, [currentMenu]);

  // Reset mobile sidebar collapsed state when main sidebar visibility changes
  useEffect(() => {
    if (!state.active) {
      setMobileSidebarCollapsed(false);
    }
  }, [state.active]);

  // Prevent body scrolling when sidebar is open on mobile
  useEffect(() => {
    const handleSidebarScroll = (e) => {
      // Only prevent propagation if the sidebar is open on mobile
      if (state.active) {
        e.stopPropagation();
      }
    };

    // Add event listeners to prevent scroll propagation
    const sidebarElement = document.getElementById('mobile-sidebar');
    if (sidebarElement) {
      sidebarElement.addEventListener('wheel', handleSidebarScroll, { passive: false });
      sidebarElement.addEventListener('touchmove', handleSidebarScroll, { passive: false });
    }

    // Lock body scroll when mobile sidebar is open
    if (state.active) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      // Clean up event listeners
      if (sidebarElement) {
        sidebarElement.removeEventListener('wheel', handleSidebarScroll);
        sidebarElement.removeEventListener('touchmove', handleSidebarScroll);
      }
      // Always reset body overflow on unmount
      document.body.style.overflow = '';
    };
  }, [state.active]);

  // Add useEffect to handle initial state based on screen size
  useEffect(() => {
    const handleResize = () => {
      // Check if it's a tablet (between 768px and 1023px)
      const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1023;
      
      if (isTablet) {
        setIsSidebarCollapsed(true);
        const mainWrapper = document.querySelector('#main-wrapper');
        if (mainWrapper) {
          mainWrapper.classList.add('menu-toggle');
        }
      }
    };

    // Run on mount
    handleResize();

    // Add event listener for resize
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsSidebarCollapsed]);

  const handleLinkClick = (e, title, parentTitle = null) => {
    e.preventDefault();
    const href = e.currentTarget.href;
    handleActive(title, parentTitle);
    closeSidebar();
    setTimeout(() => {
      window.location.href = href;
    }, 0);
  };

  const handleMouseEnter = (title) => {
    if (isSidebarCollapsed) {
      setHoveredItem(title);
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  // Handle wheel events to prevent propagation
  const handleWheel = (e) => {
    e.stopPropagation();
  };

  // Shared sidebar content component to avoid duplication
  const SidebarContent = ({ isMobile = false }) => {
    const isCollapsed = isMobile ? mobileSidebarCollapsed : isSidebarCollapsed;
    
    return (
      <>
        {/* Logo Section with Frosted Glass Effect */}
        <div className="relative h-16 flex items-center justify-center overflow-hidden">
          {/* Background with blur */}
          <div className="absolute inset-0 bg-primary-headings opacity-90"></div>
          <div className="absolute inset-0 backdrop-blur-md"></div>
          
          {/* Logo and Toggle Button Container */}
          <div className="relative z-20 flex items-center justify-center w-full">
            {/* Only show logo when sidebar is expanded */}
            {!isCollapsed && (
              <Link to="/" className="absolute left-1/2 transform -translate-x-1/2 w-[60%] flex justify-center">
                <img 
                  src={logo} 
                  alt="smarterView Logo" 
                  className="invert brightness-0"
                />
              </Link>
            )}
            
            {/* Toggle button for sidebar */}
            <div 
              onClick={isMobile ? toggleMobileSidebar : toggleSidebar}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 cursor-pointer backdrop-blur-sm border border-white/10 shadow-lg"
            >
              <FaBars className="text-white/80" />
              {/* {isCollapsed ? (
                <FaBars className="text-white/80" />
              ) : (
                <FaAngleLeft className="text-white/80" />
              )} */}
            </div>
          </div>
        </div>

        {/* Main Sidebar Content - Only show when not collapsed */}
        {!isCollapsed && (
          <div className="flex-1 bg-gradient-to-b from-primary-headings via-primary-headings to-[#0a3b3b] overflow-hidden">
            <PerfectScrollbar 
              className="h-full sidebar-scrollbar" 
              options={{ 
                suppressScrollX: true,
                wheelPropagation: false
              }}
              onWheel={handleWheel}
            >
              <div className="px-6 py-2 text-center">
              </div>
              
              <ul className="space-y-1 px-4 py-2">
                {currentMenu.map((data, index) => {
                  const isActive = state.active === data.title;
                  const isExpanded = expandedItems[data.title];
                  
                  if (data.classsChange === "menu-title") {
                    return (
                      <li key={index} className="px-2 py-3 text-xs font-semibold text-white/50 uppercase tracking-wider mt-4 text-center">
                        {data.title}
                      </li>
                    );
                  }
                  
                  return (
                    <li key={index} className="relative group">
                      {data.content && data.content.length > 0 ? (
                        <>
                          <button
                            onClick={() => toggleSubmenu(data.title)}
                            className={`w-full flex items-center justify-between px-4 py-3 text-sm rounded-sm transition-all duration-300 ${
                              isActive
                                ? "text-white bg-gradient-to-r from-primary-headings to-primary-light/20 shadow-md shadow-primary-headings/10"
                                : "text-white/80 hover:text-white hover:bg-white/5"
                            }`}
                          >
                            <div className="flex items-center">
                              <span className={`transition-all duration-300 ${isActive ? 'text-white' : 'text-white/70 group-hover:text-white/90'}`}>
                                {data.icon}
                              </span>
                              <span className="font-medium ml-3">{data.title}</span>
                            </div>
                            
                            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="text-white/80" viewBox="0 0 24 24" width="1em" height="1em">
  <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>

                          </button>
                          
                          {/* Regular dropdown */}
                          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
                            <ul className="mt-1 mb-2 ml-7 border-l border-white/10 pl-3 space-y-1">
                              {data.content.map((subData, idx) => (
                                <li key={idx} className="relative">
                                  <Link
                                    to={subData.to}
                                    className={`flex items-center py-2.5 pl-2 pr-3 text-sm rounded-lg transition-all duration-200 ${
                                      state.activeMenu === subData.title
                                        ? "text-white bg-white/5 border-l-2 border-accent-light/70"
                                        : "text-white/70 hover:text-white/90 hover:bg-white/5"
                                    }`}
                                    onClick={(e) => {
                                      e.currentTarget.blur();
                                      handleMenuActive(subData.title, data.title);
                                      closeSidebar();
                                    }}
                                  >
                                    {/* {state.activeMenu === subData.title && (
                                      <div className="absolute left-[-12px] w-1.5 h-1.5 rounded-full bg-accent-light/80"></div>
                                    )} */}
                                    {subData.icon && (
                                      <span className="text-white/70 mr-2">{subData.icon}</span>
                                    )}
                                    <span className="font-medium">{subData.title}</span>
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </>
                      ) : (
                        <Link
                          to={data.to}
                          onClick={(e) => {
                            e.currentTarget.blur();
                            handleLinkClick(e, data.title);
                          }}
                          className={`flex items-center px-4 py-3 text-sm rounded-sm transition-all duration-300 ${
                            isActive
                              ? "text-white bg-gradient-to-r from-primary-headings to-primary-light/20 shadow-md shadow-primary-headings/10"
                              : "text-white/80 hover:text-white/90 hover:bg-white/5"
                          }`}
                        >
                          <span className={`transition-all duration-300 ${isActive ? 'text-white' : 'text-white/70 group-hover:text-white/90'}`}>
                            {data.icon}
                          </span>
                          <span className="font-medium ml-3">{data.title}</span>
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </PerfectScrollbar>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        onMouseEnter={() => ChangeIconSidebar(true)}
        onMouseLeave={() => ChangeIconSidebar(false)}
        className={`fixed top-0 left-0 h-screen bg-primary-headings transition-all duration-300 ease-in-out ${
          isSidebarCollapsed ? "w-0" : "w-72"
        } hidden lg:flex flex-col z-50 shadow-2xl`}
        onWheel={handleWheel}
      >
        <SidebarContent />
      </div>

      {/* Mobile/Tablet Sidebar */}
      <div 
        className={`fixed top-0 left-0 h-screen bg-primary-headings transition-all duration-300 ease-in-out ${
          isSidebarCollapsed ? "-translate-x-full" : "translate-x-0"
        } lg:hidden flex flex-col z-50 shadow-2xl w-72`}
      >
        <SidebarContent isMobile={true} />
      </div>

      {/* Backdrop for mobile/tablet */}
      {!isSidebarCollapsed && (
        <div 
          className="fixed inset-0 bg-black/50 lg:hidden z-40"
          onClick={toggleMobileSidebar}
        />
      )}
    </>
  );
};

export default SideBar;
